import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import Switch from 'components/ui/Switch';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import { KEY_BOARD, MODAL_TYPES } from 'constants/general';
import COLOR from 'constants/color';
import { Add, TrashOutlined, Globe } from 'assets/icons/revamp';
import Card from 'components/ui/Card';
import { t } from 'services/i18n.service';
import {
  updateMoreOptionIsUseWebsiteChecked,
  updateMoreOptionURLs
} from 'store/reducer/modal/slice';
import { moreOptionsWebsitesSelector } from 'store/reducer/setting/selectors';
import BaseButton from 'components/ui/BaseButton';
import { toast } from 'react-toastify';
import { DEFAULT_HTML_ATTRIBUTES } from 'assets/data';

const Websites: React.FC = () => {
  const dispatch = useAppDispatch();
  const [websiteUrlInput, setWebsiteUrlInput] = useState('');

  const { activity, isUseWebsiteChecked } = useAppSelectorShallow(
    moreOptionsWebsitesSelector
  );

  const allowedWebsites = useMemo(
    () => activity?.allowed_urls ?? [],
    [activity?.allowed_urls]
  );

  useEffect(() => {
    dispatch(
      updateMoreOptionIsUseWebsiteChecked(Boolean(allowedWebsites.length))
    );
  }, [allowedWebsites.length, dispatch]);

  const handleAddWebsite = () => {
    if (websiteUrlInput.trim()) {
      const url = websiteUrlInput.trim().replace(/^https?:\/\//, '');
      if (!allowedWebsites.includes(url)) {
        dispatch(
          updateMoreOptionURLs({
            modal_type: MODAL_TYPES.ALLOWED_URLS,
            urls: [...allowedWebsites, url]
          })
        );
        setWebsiteUrlInput('');
      } else {
        toast.error(
          t('tabs_revamp.allowed_apps_tab.website_url_already_added')
        );
      }
    }
  };

  const handleDeleteWebsite = (url: string) => {
    dispatch(
      updateMoreOptionURLs({
        modal_type: MODAL_TYPES.ALLOWED_URLS,
        urls: allowedWebsites.filter((site) => site !== url)
      })
    );
  };

  return (
    <Card className='flex flex-col ~gap-3/4'>
      <div className='flex flex-col ~gap-1/2'>
        <div className='flex items-center ~gap-3/6'>
          <h3 className='~text-xs/base font-semibold flex items-center ~gap-1/2'>
            <Globe styles='~w-4/5 ~h-4/5' fill={COLOR.PRIMARY.LIGHT_900} />
            {t('tabs_revamp.allowed_apps_tab.allow_websites')}
          </h3>
          <Switch
            checked={isUseWebsiteChecked}
            onChange={(checked) =>
              dispatch(updateMoreOptionIsUseWebsiteChecked(checked))
            }
            size='medium'
            variant='default'
          />
        </div>
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400 ~ml-0/0'>
          {t('tabs_revamp.allowed_apps_tab.allow_websites_description')}
        </p>
      </div>

      {isUseWebsiteChecked && (
        <div className='flex flex-col ~gap-3/4'>
          {allowedWebsites.length > 0 && (
            <div className='flex flex-wrap items-center ~gap-2/3'>
              {allowedWebsites.map((url) => (
                <div
                  key={url}
                  className='flex items-center ~gap-1.5/2 ~px-2/3 ~py-1/1.5 bg-gray-100 dark:bg-primary-dark-50 rounded-full border border-primary-dark-200 dark:border-primary-900'>
                  <span className='~text-xs/sm text-black dark:text-white'>
                    {url}
                  </span>
                  <button
                    onClick={() => handleDeleteWebsite(url)}
                    className='flex-shrink-0 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full ~p-0.5/1 transition-colors'
                    aria-label={t(
                      'tabs_revamp.allowed_apps_tab.remove_website',
                      {
                        url
                      }
                    )}>
                    <TrashOutlined
                      className='~w-3/5 ~h-3/5'
                      fill={COLOR.SEMANTIC.ERROR}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className='flex flex-col ~gap-2/3'>
            <FloatingLabelInput
              type='text'
              placeholder={t('tabs_revamp.allowed_apps_tab.enter_url')}
              value={websiteUrlInput}
              onChange={(e) => setWebsiteUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === KEY_BOARD.ENTER.KEY) {
                  handleAddWebsite();
                }
              }}
              size='small'
              showLabel={true}
              label=''
              {...DEFAULT_HTML_ATTRIBUTES}
            />
            <div className='flex justify-end ~gap-2/3'>
              <BaseButton
                title={t('tabs_revamp.allowed_apps_tab.add_url')}
                onClick={handleAddWebsite}
                variant='secondary'
                icon={
                  <Add styles='~w-3/4 ~h-3/4' fill={COLOR.PRIMARY.LIGHT_900} />
                }
                size='sm'
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default Websites;
